import { Link } from "react-router-dom";
import { MapPin, Eye, Clock, Zap, Star, ShieldCheck, Crown } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ro, enUS } from "date-fns/locale";
import { useLanguage } from "../i18n/LanguageContext";

export default function AdCard({ ad }) {
  const { t, language } = useLanguage();
  const {
    ad_id,
    title,
    price,
    price_type,
    currency,
    city_name,
    localitate,
    category_name,
    category_color,
    images = [],
    views = 0,
    created_at,
    is_boosted,
    is_promoted,
    is_premium_seller_ad,
    user_rating,
    user_reviews,
    user_badges = []
  } = ad;

  const isVerified = user_badges?.includes("verified_identity");
  const isTopSeller = user_badges?.includes("top_seller");
  const isPremiumSeller = user_badges?.includes("premium_seller") || is_premium_seller_ad;

  const currencySymbol = (currency || "RON") === "EUR" ? "€" : "RON";

  const formatPrice = () => {
    if (price_type === "free") return t('free');
    if (price_type === "negotiable") return price ? `${price.toLocaleString("ro-RO")} ${currencySymbol} (${t('negotiable')})` : t('priceNegotiable');
    return price ? `${price.toLocaleString("ro-RO")} ${currencySymbol}` : t('priceOnRequest');
  };

  const dateLocale = language === 'en' ? enUS : ro;
  const timeAgo = created_at 
    ? formatDistanceToNow(new Date(created_at), { addSuffix: true, locale: dateLocale })
    : "";

  const defaultImage = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400&h=300&fit=crop";

  return (
    <Link
      to={`/ad/${ad_id}`}
      className={`group relative overflow-hidden rounded-xl sm:rounded-2xl bg-[#0A0A0A] border transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${
        is_boosted
          ? "border-fuchsia-500/30 shadow-[0_0_30px_rgba(217,70,239,0.15)]"
          : isPremiumSeller
          ? "border-amber-500/30 shadow-[0_0_25px_rgba(245,158,11,0.12)]"
          : "border-white/5 hover:border-white/10"
      }`}
      data-testid={`ad-card-${ad_id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-900">
        <img
          src={images[0] ? `${images[0]}?w=400&q=75` : defaultImage}
          srcSet={images[0] ? `${images[0]}?w=300&q=75 300w, ${images[0]}?w=400&q=80 400w, ${images[0]}?w=600&q=80 600w` : undefined}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.target.src = defaultImage;
          }}
        />
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Badges */}
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 flex flex-wrap gap-1 sm:gap-2">
          {isVerified && (
            <span className="flex items-center gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-green-600 text-white text-[10px] sm:text-xs font-medium shadow-lg" title={t('verifiedSeller')}>
              <ShieldCheck className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">{t('verified')}</span>
            </span>
          )}
          {is_promoted && (
            <span className="flex items-center gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-blue-600 text-white text-[10px] sm:text-xs font-medium shadow-lg">
              <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">{t('promoted')}</span>
            </span>
          )}
          {is_boosted && (
            <span className="flex items-center gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-fuchsia-600 text-white text-[10px] sm:text-xs font-medium shadow-lg">
              <Zap className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">{t('boosted')}</span>
            </span>
          )}
          {isPremiumSeller && !is_boosted && !is_promoted && (
            <span
              className="flex items-center gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-black text-[10px] sm:text-xs font-bold shadow-lg"
              title="Vânzător Pro"
            >
              <Crown className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">PRO</span>
            </span>
          )}
        </div>

        {/* Category badge */}
        {category_name && (
          <span 
            className="absolute top-2 sm:top-3 right-2 sm:right-3 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium backdrop-blur-sm hidden sm:block"
            style={{ 
              backgroundColor: `${category_color}20`,
              color: category_color,
              border: `1px solid ${category_color}40`
            }}
          >
            {category_name}
          </span>
        )}

        {/* Price */}
        <div className="absolute bottom-3 left-3">
          <span className={`text-xl font-bold ${price_type === "free" ? "text-emerald-400" : "text-white"}`}>
            {formatPrice()}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-white font-medium line-clamp-2 mb-3 group-hover:text-blue-400 transition-colors">
          {title}
        </h3>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5 text-slate-400">
            <MapPin className="w-4 h-4" />
            <span>{city_name || localitate || "România"}</span>
          </div>

          <div className="flex items-center gap-3 text-slate-500">
            {user_rating > 0 && (
              <span className="flex items-center gap-1 text-yellow-400">
                <Star className="w-3.5 h-3.5 fill-yellow-400" />
                <span className="text-xs">{user_rating.toFixed(1)}</span>
              </span>
            )}
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {views}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
